import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  TrendingUp, TrendingDown, Minus, Search, Clock,
  Sparkles, ChevronRight, Newspaper, AlertCircle
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import InfoTip from '@/components/InfoTip';
import { toast } from 'sonner';

/* Generate 3m history + 3m forecast for a fresh component */
function genChartData(base, pctChange) {
  const today = new Date();
  const data = [];
  // Past 3 months
  for (let i = 90; i > 0; i--) {
    const d = new Date(today); d.setDate(d.getDate() - i);
    data.push({
      date: `${d.getMonth() + 1}/${d.getDate()}`,
      price: +(base * (1 + (Math.random() - 0.45) * 0.08 - (90 - i) / 90 * pctChange / 200)).toFixed(3),
      type: 'history',
    });
  }
  // Today
  data.push({ date: 'Today', price: base, type: 'today' });
  // Next 3 months
  const target = base * (1 + pctChange / 100);
  for (let i = 1; i <= 90; i++) {
    const d = new Date(today); d.setDate(d.getDate() + i);
    data.push({
      date: `${d.getMonth() + 1}/${d.getDate()}`,
      price: +(base + ((target - base) * (i / 90)) + (Math.random() - 0.5) * base * 0.03).toFixed(3),
      type: 'forecast',
    });
  }
  return data;
}

/* Common MPN suggestions */
const SUGGESTIONS = [
  'STM32F407VGT6', 'ESP32-WROOM-32E', 'NRF52840-QIAA-R', 'MT48LC16M16A2',
  'GRM188R71H104KA93D', 'TPS54331DR', 'LAN8720A-CP-TR', 'SN65HVD230DR',
];

/* Mock AI prediction engine */
function predictComponent(mpn) {
  const knownData = {
    'STM32F407VGT6': { manufacturer: 'STMicroelectronics', category: 'MCU', price: 12.50, change: 15, confidence: 92, reason: 'Supply shortage due to increased automotive demand. ST Micro fab utilization at 95%. Expect 12-15% price increase over 90 days.' },
    'ESP32-WROOM-32E': { manufacturer: 'Espressif', category: 'Wireless IC', price: 2.80, change: -12, confidence: 80, reason: 'New BK7231 and WB2 competitors entering market. Espressif ramping ESP32-S3, older -32E inventory building. Prices expected to decline.' },
    'NRF52840-QIAA-R': { manufacturer: 'Nordic Semiconductor', category: 'MCU', price: 5.75, change: 16, confidence: 78, reason: 'Nordic capacity constrained at TSMC. Lead times extended to 20 weeks. BLE SoC demand from wearables accelerating price increase.' },
    'MT48LC16M16A2': { manufacturer: 'Micron', category: 'Memory IC', price: 3.20, change: 22, confidence: 85, reason: 'Global DRAM shortage — Taiwan fab utilization at 97%. Server and AI training demand competing with consumer electronics for DRAM capacity.' },
    'GRM188R71H104KA93D': { manufacturer: 'Murata', category: 'Capacitor', price: 0.025, change: -8, confidence: 78, reason: 'New MLCC production capacity from Murata and Samsung coming online in Q2. EV demand partially offsets but oversupply expected for standard 0402 sizes.' },
    'TPS54331DR': { manufacturer: 'Texas Instruments', category: 'Power IC', price: 1.65, change: -5, confidence: 88, reason: 'TI expanding broad-market power IC inventory. Stable demand, no constraints. Slight downward pressure from competition.' },
    'LAN8720A-CP-TR': { manufacturer: 'Microchip', category: 'Ethernet PHY', price: 1.85, change: -5, confidence: 71, reason: 'Component entering NRND status. Inventory being cleared. Replacement LAN8742A gaining traction. Expect mild price decline as last-buy approaches.' },
    'SN65HVD230DR': { manufacturer: 'Texas Instruments', category: 'CAN Transceiver', price: 0.95, change: -3, confidence: 88, reason: 'Commodity CAN transceiver. Multiple new entrants. No supply constraints. Prices stable with slight downward trend.' },
  };
  const key = mpn.toUpperCase().trim();
  const match = Object.keys(knownData).find(k => key.includes(k) || k.includes(key));
  if (match) {
    const d = knownData[match];
    return { found: true, mpn: match, ...d, data: genChartData(d.price, d.change), trend: d.change > 0 ? 'up' : d.change < 0 ? 'down' : 'stable' };
  }
  // Unknown component — generate random prediction
  const price = +(Math.random() * 10 + 0.5).toFixed(2);
  const change = Math.round((Math.random() - 0.5) * 30);
  return {
    found: true, mpn: key, manufacturer: 'Unknown', category: 'Uncategorized', price, change,
    confidence: Math.round(45 + Math.random() * 25), trend: change > 0 ? 'up' : change < 0 ? 'down' : 'stable',
    reason: `Limited market data for ${key}. Prediction based on category-level pricing trends and distributor index. Confidence is moderate — recommend verifying with live quotes.`,
    data: genChartData(price, change),
  };
}

const trendIcon = (t) => ({ up: <TrendingUp className="h-5 w-5 text-red-500" />, down: <TrendingDown className="h-5 w-5 text-green-500" /> }[t] || <Minus className="h-5 w-5 text-gray-500" />);
const trendColor = (t) => ({ up: 'text-red-600 bg-red-50', down: 'text-green-600 bg-green-50' }[t] || 'text-gray-600 bg-gray-50');

const MarketPulse = () => {
  const [searchInput, setSearchInput] = useState('');
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);

  const handleSearch = (mpn) => {
    const query = (mpn || searchInput).trim();
    if (!query) { toast.error('Please enter a component MPN'); return; }
    setLoading(true);
    setSearchInput(query);
    // Simulate AI processing
    setTimeout(() => {
      const result = predictComponent(query);
      setPrediction(result);
      setLoading(false);
      if (!recentSearches.includes(query)) setRecentSearches(prev => [query, ...prev].slice(0, 5));
      toast.success(`AI forecast generated for ${result.mpn}`);
    }, 1200);
  };

  return (
    <Layout title="AI Price Forecast" subtitle="Search new components without past purchase history for AI-powered price predictions">
      <div className="space-y-6" data-testid="market-pulse-page">
        {/* Search Bar */}
        <Card className="bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="text-center mb-4">
              <Sparkles className="h-10 w-10 text-blue-600 mx-auto mb-2" />
              <h2 className="text-xl font-bold text-gray-900">Search a New Component</h2>
              <p className="text-sm text-gray-600">Enter any MPN you haven't purchased before — get AI-powered past 3-month market trends and next 3-month price forecast with confidence index</p>
            </div>
            <div className="flex items-center gap-3 max-w-2xl mx-auto">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  placeholder="Enter component MPN (e.g., STM32F407VGT6)"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  className="pl-11 h-12 text-lg bg-white"
                  data-testid="forecast-search-input"
                />
              </div>
              <Button onClick={() => handleSearch()} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white h-12 px-6" data-testid="forecast-search-btn">
                {loading ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" /> : <><Sparkles className="h-5 w-5 mr-2" /> Forecast</>}
              </Button>
            </div>
            {/* Suggestions */}
            <div className="flex flex-wrap gap-2 justify-center mt-4">
              {SUGGESTIONS.map(s => (
                <Button key={s} variant="outline" size="sm" className="text-xs font-mono bg-white" onClick={() => { setSearchInput(s); handleSearch(s); }} data-testid={`suggestion-${s}`}>
                  {s}
                </Button>
              ))}
            </div>
            {recentSearches.length > 0 && (
              <div className="flex items-center gap-2 justify-center mt-3">
                <span className="text-xs text-gray-400">Recent:</span>
                {recentSearches.map(s => (
                  <Badge key={s} variant="outline" className="text-xs font-mono cursor-pointer hover:bg-gray-100" onClick={() => { setSearchInput(s); handleSearch(s); }}>{s}</Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Result */}
        {loading && (
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="py-16 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Analyzing market data for <span className="font-mono font-bold">{searchInput}</span>...</p>
              <p className="text-sm text-gray-400 mt-1">Querying 50,000+ data points across global distributors</p>
            </CardContent>
          </Card>
        )}

        {prediction && !loading && (
          <>
            {/* Prediction Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="md:col-span-2 bg-white border-gray-200 shadow-sm">
                <CardContent className="pt-5">
                  <div className="flex items-center gap-3">
                    <div className={`inline-flex items-center gap-1 px-3 py-2 rounded-full ${trendColor(prediction.trend)}`}>
                      {trendIcon(prediction.trend)}
                      <span className="text-lg font-bold">{prediction.change > 0 ? '+' : ''}{prediction.change}%</span>
                    </div>
                    <div>
                      <h3 className="font-mono font-bold text-gray-900 text-lg">{prediction.mpn}</h3>
                      <p className="text-sm text-gray-500">{prediction.manufacturer} | {prediction.category}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardContent className="pt-5">
                  <p className="text-xs text-gray-500 uppercase">Current Price</p>
                  <p className="text-2xl font-bold text-gray-900">${prediction.price.toFixed(2)}</p>
                  <p className="text-xs text-gray-400 mt-1">Predicted: <span className={prediction.trend === 'up' ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>${(prediction.price * (1 + prediction.change / 100)).toFixed(2)}</span></p>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-blue-200 shadow-sm">
                <CardContent className="pt-5">
                  <p className="text-xs text-blue-600 uppercase flex items-center">Confidence Index <InfoTip text="AI confidence in this prediction based on data availability, historical accuracy, and market signal strength." /></p>
                  <p className="text-2xl font-bold text-blue-700">{prediction.confidence}%</p>
                  <p className="text-xs text-blue-400 mt-1">3-month forecast</p>
                </CardContent>
              </Card>
            </div>

            {/* Chart */}
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="forecast-chart">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  Price Trend — Past 3 Months + Next 3 Months
                  <InfoTip text="Left of the 'Today' line is historical pricing. Right is the AI-predicted forecast. The shaded area shows the prediction range." />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={prediction.data}>
                    <defs>
                      <linearGradient id="colorHist" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6b7280" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#6b7280" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorForecast" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={prediction.trend === 'up' ? '#ef4444' : '#16a34a'} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={prediction.trend === 'up' ? '#ef4444' : '#16a34a'} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 9 }} interval={Math.floor(prediction.data.length / 8)} />
                    <YAxis domain={['auto', 'auto']} tick={{ fontSize: 10 }} width={50} tickFormatter={v => `$${v}`} />
                    <Tooltip formatter={v => [`$${Number(v).toFixed(3)}`, 'Price']} />
                    <ReferenceLine x="Today" stroke="#3b82f6" strokeWidth={2} strokeDasharray="4 4" label={{ value: 'Today', position: 'top', fill: '#3b82f6', fontSize: 11 }} />
                    <Area type="monotone" dataKey="price" stroke={prediction.trend === 'up' ? '#ef4444' : '#16a34a'} fill={`url(#colorForecast)`} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
                <div className="flex items-center justify-center gap-6 mt-4 text-sm">
                  <div className="flex items-center gap-2"><div className="w-8 h-0.5 bg-gray-400" /><span className="text-gray-600">Historical (3m)</span></div>
                  <div className="flex items-center gap-2"><div className="w-8 h-0.5 bg-blue-500 border-dashed border-t-2" /><span className="text-gray-600">Today</span></div>
                  <div className="flex items-center gap-2"><div className="w-8 h-0.5" style={{ backgroundColor: prediction.trend === 'up' ? '#ef4444' : '#16a34a' }} /><span className="text-gray-600">AI Forecast (3m)</span></div>
                </div>
              </CardContent>
            </Card>

            {/* AI Analysis */}
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="forecast-analysis">
              <CardHeader><CardTitle className="text-gray-900 flex items-center gap-2"><Sparkles className="h-5 w-5 text-blue-600" /> AI Analysis</CardTitle></CardHeader>
              <CardContent>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-900">{prediction.reason}</p>
                </div>
                <div className="flex gap-3 mt-4">
                  <Button className="bg-green-600 hover:bg-green-700 text-white" data-testid="set-price-alert-btn"
                    onClick={() => toast.success(`Price alert set for ${prediction.mpn} — you'll be notified when price changes significantly`)}>
                    <AlertCircle className="h-4 w-4 mr-2" /> Set Price Alert
                  </Button>
                  <Button variant="outline" data-testid="view-market-news-btn"
                    onClick={() => { window.open(`https://news.google.com/search?q=${encodeURIComponent(prediction.mpn + ' ' + prediction.manufacturer + ' semiconductor')}`, '_blank'); toast.info('Opening market news...'); }}>
                    <Newspaper className="h-4 w-4 mr-2" /> View Market News
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {!prediction && !loading && (
          <Card className="bg-gray-50 border-gray-200 shadow-sm" data-testid="forecast-placeholder">
            <CardContent className="py-20 text-center">
              <Sparkles className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600">Enter a component MPN above</h3>
              <p className="text-sm text-gray-500 mt-1">Get AI-powered price prediction with 3-month history and 3-month forecast</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default MarketPulse;
